import { EventEmitter } from 'events';
import {
  ExceptionData,
  SessionJoinData,
  SessionReadyData,
  TranscriptActor,
  TranscriptPayload,
  WebSocketCallServiceConfig,
} from './types';
import { CallActors } from '@shared/constants/recipient-status.constants';
import { OpusDecoder } from 'opus-decoder';
import { toast } from 'react-toastify';
import i18n from 'i18next';

import { CALLER_AUDIO_CAPTURE } from '@shared/constants/audioCapture';
import { ensureFreshAccessToken, refreshToken } from '@shared/store/api/baseApi';
import { initOpus, OpusAPI, OpusEncoder } from '../Opus';
import { normalizeToF32 } from './utils';
import {
  CallWsEvent,
  DEFAULT_OPUS_PACKAGE_CONFIG,
  SCRIPT_PROCESSOR_BUFFER_SIZE,
  WS_POLICY_VIOLATION_CLOSE_CODE,
  WsErrorCode,
} from './consts';

/**
 * WebSocketCallService
 * - Captures mic audio via AudioWorklet (mono, 48kHz, 20ms, 960 samples)
 * - Encodes to Opus and streams frames over WebSocket
 * - Optionally decodes received Opus to PCM and plays via AudioWorklet
 * - Optional self-monitor: immediately decode and play locally what we encode
 */
export class WebSocketCallService extends EventEmitter {
  private ws: WebSocket | null = null;

  private serviceUrl: string;
  private token?: string;
  private campaignId: string;
  private customerName: string;

  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private captureNode: AudioWorkletNode | null = null;
  private captureCleanup?: () => void;
  private playbackNode: AudioWorkletNode | null = null;
  private audioGainNode: GainNode | null = null;
  private mediaOutDest: MediaStreamAudioDestinationNode | null = null;
  private remoteAudioEl: HTMLAudioElement | null = null;
  private playbackClock = 0;
  private micReader: ReadableStreamDefaultReader | null = null;
  // Mic samples are re-chunked into fixed 20ms Opus frames regardless of the browser's chunk size
  private micFrame = new Int16Array(DEFAULT_OPUS_PACKAGE_CONFIG.frameSize);
  private micFrameLength = 0;

  private opusApi?: OpusAPI;
  private opusEncoder?: OpusEncoder;
  private opusDecoder?: OpusDecoder;
  private isRecording = false;
  private isSessionReady = false;
  private callId: string | null = null;
  private hasRetriedAuth = false;
  private lastExceptionType: string | null = null;

  private lifecycle = 0;
  private selfMonitor = false; // enable local monitor (loopback)

  constructor(private config: WebSocketCallServiceConfig) {
    super();
    this.audioContext = config.preparedAudioContext ?? null;
    this.serviceUrl = config.serviceUrl;
    this.campaignId = config.campaignId;
    this.customerName = config.customerName ?? 'Anonymous';

    if (config.onConnectedToServer) this.on('connectedToServer', config.onConnectedToServer);
    if (config.onSessionReady) this.on('sessionReady', config.onSessionReady);
    if (config.onTranscriptReceived) this.on('transcriptReceived', config.onTranscriptReceived);
    if (config.onError) this.on('error', config.onError);
    if (config.onDisconnect) this.on('disconnect', config.onDisconnect);

    this.on('connectionReady', async (data: SessionJoinData) => {
      try {
        await this.joinSession(data);
      } catch (e) {
        this.emit('error', e);
      }
    });

    this.on('audioReceived', (opusFrame: ArrayBuffer) => {
      if (!this.opusDecoder) return;
      const raw = this.decodeWithopusDecoder(new Uint8Array(opusFrame));
      if (!raw) return;
      const f32 = normalizeToF32(raw);
      if (!f32 || !this.audioContext || !this.audioGainNode) return;
      this.playPcmF32(f32);
    });
  }

  attachOutputElement(el: HTMLAudioElement) {
    this.remoteAudioEl = el;
    if (this.mediaOutDest) {
      this.remoteAudioEl.srcObject = this.mediaOutDest.stream;
      this.remoteAudioEl.autoplay = true;
      this.remoteAudioEl.play().catch(() => {
        /* ignore */
      });
    }
  }
  /**
   * Connect to WebSocket backend and start session
   */
  async connect(): Promise<void> {
    if (this.ws) return;

    const epoch = this.lifecycle;
    this.token = this.config.visitor?.token ?? (await ensureFreshAccessToken()) ?? undefined;
    if (epoch !== this.lifecycle) return;
    if (!this.token) {
      this.emit('disconnect');
      return;
    }

    this.openSocket(this.token);
  }

  private openSocket(token: string): void {
    const url = this.config.visitor
      ? new URL(this.config.visitor.url)
      : new URL(`${this.serviceUrl}/v1/rtc-test`, window.location.href);
    if (this.config.visitor) {
      url.searchParams.set('role', 'both');
      url.searchParams.set('session_id', this.config.visitor.sessionId);
    }
    const ws = new WebSocket(url.toString(), ['bearer', token]);
    this.ws = ws;
    ws.binaryType = 'arraybuffer';

    const epoch = this.lifecycle;
    const ownsSocket = () => this.ws === ws && epoch === this.lifecycle;
    ws.onopen = () => {
      if (ownsSocket()) this.emit('connectedToServer');
    };
    ws.onmessage = (ev: MessageEvent) => {
      if (ownsSocket()) this.handleServerMessage(ev);
    };
    ws.onerror = () => {
      if (ownsSocket()) this.emit('error', new Error('WebSocket error'));
    };

    ws.onclose = (ev: CloseEvent) => {
      if (!ownsSocket()) return;
      this.ws = null;

      const rejectedToken =
        ev.code === WS_POLICY_VIOLATION_CLOSE_CODE &&
        this.lastExceptionType === WsErrorCode.AuthenticationError;

      if (rejectedToken && !this.config.visitor && !this.hasRetriedAuth) {
        this.hasRetriedAuth = true;
        void this.reconnectWithFreshToken();
        return;
      }

      void this.endCall();
      this.emit('disconnect');
    };
  }

  private async reconnectWithFreshToken(): Promise<void> {
    const epoch = this.lifecycle;
    const token = await refreshToken();
    if (epoch !== this.lifecycle) return;

    if (!token) {
      void this.endCall();
      this.emit('disconnect');
      return;
    }

    this.token = token;
    this.openSocket(token);
  }

  /**
   * Send JSON over WS
   */
  private send(event: string, data: object) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ event, data }));
  }

  /**
   * Join ongoing session
   */
  private async joinSession(data: SessionJoinData): Promise<void> {
    this.send(CallWsEvent.SessionJoin, data);
  }

  /**
   * Handle all messages from server
   */
  private handleServerMessage(ev: MessageEvent) {
    const res = ev.data;
    if (res instanceof ArrayBuffer) {
      // fallback to Opus
      this.emit('audioReceived', res);
      return;
    }

    try {
      const msg = JSON.parse(res as string);

      const { event, data } = msg || {};
      switch (event) {
        case CallWsEvent.SessionReady: {
          const info: SessionReadyData = data;
          this.isSessionReady = true;
          this.callId = info.callId;
          this.emit('sessionReady', info);
          break;
        }
        case CallWsEvent.AgentTranscript: {
          this.emitTranscript(CallActors.AGENT, data);
          break;
        }
        case CallWsEvent.CustomerTranscript: {
          this.emitTranscript(CallActors.CUSTOMER, data);
          break;
        }
        case CallWsEvent.ConnectionReady: {
          if (this.config.visitor) {
            this.send(CallWsEvent.SessionJoin, {
              includeTranscript: true,
              sampleRate: DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
              codec: 'opus',
            });
            break;
          }
          this.emit('connectionReady', {
            campaignId: this.campaignId,
            customer: { name: this.customerName },
            includeTranscript: true,
            sampleRate: DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
            codec: 'opus',
          });
          break;
        }
        case 'customer.text.accepted': {
          if (typeof data?.id === 'string') this.config.onTextAccepted?.(data.id);
          break;
        }
        case CallWsEvent.SessionClose: {
          if (!this.config.visitor)
            toast.info(i18n.t('agentPage.status.callEndedByBackend'), {
              autoClose: 5000,
            });
          this.endCall();
          break;
        }
        case CallWsEvent.Exception: {
          const exception = data as ExceptionData | undefined;
          const raw = exception?.error;
          const errorMessage = (typeof raw === 'string' ? raw : raw?.message) ?? 'Server error';

          this.lastExceptionType = exception?.type ?? null;

          if (
            !this.config.visitor &&
            this.lastExceptionType === WsErrorCode.AuthenticationError &&
            !this.hasRetriedAuth
          ) {
            break;
          }

          if (!this.config.visitor) toast.error(errorMessage, { autoClose: 5000 });
          this.emit(
            'error',
            new Error(this.config.visitor ? 'The conversation could not continue.' : errorMessage),
          );
          break;
        }
        default:
          break;
      }
    } catch {}
  }

  /**
   * Forward a transcript chunk together with the side of the conversation it came from
   */
  private emitTranscript(actor: TranscriptActor, data: TranscriptPayload): void {
    // The backend streams text chunks and closes an utterance with { text: '', final: true },
    // so an empty payload is only meaningful when it is the final one.
    if (!data || (!data.text && !data.final)) return;
    this.emit('transcriptReceived', { ...data, actor });
  }

  /**
   * Initialize audio capture, encoder, decoders and playback worklets
   */
  async initializeAudioCapture(epoch = this.lifecycle): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: DEFAULT_OPUS_PACKAGE_CONFIG.channels,
          sampleRate: DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          ...CALLER_AUDIO_CAPTURE,
        },
      });

      if (epoch !== this.lifecycle) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      this.mediaStream = stream;
      this.audioContext ??= new AudioContext({
        sampleRate: DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
        latencyHint: 'interactive',
      });
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      if (epoch !== this.lifecycle) return;

      this.mediaOutDest = this.audioContext.createMediaStreamDestination();

      this.audioGainNode = this.audioContext.createGain();
      this.audioGainNode.gain.value = 0.8;
      this.audioGainNode.connect(this.mediaOutDest);

      if (!this.remoteAudioEl) {
        this.remoteAudioEl = new Audio();
        this.remoteAudioEl.autoplay = true;
      }
      this.remoteAudioEl.srcObject = this.mediaOutDest.stream;
      this.remoteAudioEl.play().catch(() => {
        /* ignore autoplay gate */
      });

      const opus = await initOpus();
      if (epoch !== this.lifecycle) return;
      this.opusApi = opus;
      this.opusEncoder = this.opusApi.createEncoder(
        DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
        DEFAULT_OPUS_PACKAGE_CONFIG.channels,
        DEFAULT_OPUS_PACKAGE_CONFIG.applicationVoip,
      );

      try {
        const decoder =
          (await (OpusDecoder as any)?.create?.({
            channels: DEFAULT_OPUS_PACKAGE_CONFIG.channels,
            sampleRate: DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
          })) ??
          new (OpusDecoder as any)(
            DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
            DEFAULT_OPUS_PACKAGE_CONFIG.channels,
          );
        if (epoch !== this.lifecycle) {
          decoder.free?.();
          return;
        }
        this.opusDecoder = decoder;
      } catch (error) {
        if (epoch !== this.lifecycle) return;
        console.error('Failed to initialize Opus decoder:', error);
        this.opusDecoder = undefined;
      }

      if (epoch !== this.lifecycle || !this.mediaStream) return;
      await this.readMicLoop(this.mediaStream);
      if (epoch !== this.lifecycle) return;
      this.isRecording = true;
    } catch (error) {
      console.error('Failed to initialize audio capture:', error);
      throw error;
    }
  }

  private playPcmF32(f32: Float32Array): void {
    if (!this.audioContext || !this.audioGainNode) return;
    try {
      const ctx = this.audioContext;
      const buffer = ctx.createBuffer(
        DEFAULT_OPUS_PACKAGE_CONFIG.channels,
        f32.length,
        DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
      );
      buffer.copyToChannel(f32 as Float32Array<ArrayBuffer>, 0, 0);

      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(this.audioGainNode);

      const now = ctx.currentTime;
      if (this.playbackClock < now + 0.05) this.playbackClock = now + 0.05;
      src.start(this.playbackClock);
      this.playbackClock += buffer.duration;
    } catch (e) {
      console.warn('playPcmF32 failed', e);
    }
  }

  /**  read mic MediaStreamTrackProcessor (Chrome/Edge/Opera), fallback to ScriptProcessor */
  private async readMicLoop(stream: MediaStream): Promise<void> {
    const track = stream.getAudioTracks()[0];
    const wantRate = DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate;

    const hasTrackProcessor = typeof (window as any).MediaStreamTrackProcessor === 'function';

    if (hasTrackProcessor) {
      const Processor = (window as any).MediaStreamTrackProcessor;
      const processor = new Processor({ track });
      const reader = processor.readable.getReader();
      this.micReader = reader;
      const epoch = this.lifecycle;
      const ownsCapture = () => this.micReader === reader && epoch === this.lifecycle;
      const pump = async () => {
        try {
          while (ownsCapture()) {
            const { value, done } = await reader.read();
            if (done) return;
            if (!value) continue;
            const audioData: any = value;
            try {
              if (!ownsCapture()) return;
              const rate = audioData.sampleRate || wantRate;
              const tmp = new Float32Array(audioData.numberOfFrames);
              audioData.copyTo(tmp, { planeIndex: 0, format: 'f32' });
              let f32 = tmp;
              if (rate !== wantRate) {
                const ratio = wantRate / rate,
                  outLen = Math.round(f32.length * ratio),
                  out = new Float32Array(outLen);
                for (let i = 0; i < outLen; i++)
                  out[i] = f32[Math.min(f32.length - 1, Math.round(i / ratio))] || 0;
                f32 = out;
              }
              this.pushMicSamples(f32);
            } finally {
              audioData.close();
            }
          }
        } catch {
          if (ownsCapture())
            this.emit('error', new Error('Microphone capture stopped unexpectedly'));
        }
      };
      void pump();
    } else {
      if (!this.audioContext) return;
      const ctx = this.audioContext;
      const sourceNode = ctx.createMediaStreamSource(stream);
      const spn = ctx.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER_SIZE, 1, 1);
      // WebKit only fires onaudioprocess when the node reaches the destination; mute it to avoid echo
      const sink = ctx.createGain();
      sink.gain.value = 0;
      sourceNode.connect(spn);
      spn.connect(sink);
      sink.connect(ctx.destination);

      const epoch = this.lifecycle;
      this.captureCleanup = () => {
        spn.onaudioprocess = null;
        for (const node of [sourceNode, spn, sink]) {
          try {
            node.disconnect();
          } catch {}
        }
      };
      spn.onaudioprocess = (ev) => {
        if (epoch !== this.lifecycle) return;
        this.pushMicSamples(ev.inputBuffer.getChannelData(0));
      };
    }
  }

  /**
   * Accumulate 48kHz mic samples and emit one Opus frame per full 20ms (960 samples)
   */
  private pushMicSamples(f32: Float32Array): void {
    const frameSize = this.micFrame.length;
    for (let i = 0; i < f32.length; i++) {
      const s = Math.max(-1, Math.min(1, f32[i]));
      this.micFrame[this.micFrameLength++] = (s * 32767) | 0;
      if (this.micFrameLength === frameSize) {
        this.encodeAndSendMicFrame(this.micFrame);
        this.micFrameLength = 0;
      }
    }
  }

  private encodeAndSendMicFrame(pcm16: Int16Array): void {
    if (!this.opusEncoder) return;
    const packet = this.opusEncoder.encode(pcm16);
    if (!packet) return;

    if (this.isSessionReady && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendAudioData(packet.buffer as ArrayBuffer);
    }

    if (this.selfMonitor && this.opusDecoder) {
      const raw = this.decodeWithopusDecoder(packet);
      const smF32 = raw ? normalizeToF32(raw) : null;
      if (smF32) this.playPcmF32(smF32);
    }
  }

  /**
   * Convert module source (string) to a blob URL for AudioWorklet
   */
  private toWorkletUrl(source: string) {
    const blob = new Blob([source], { type: 'application/javascript' });
    return URL.createObjectURL(blob);
  }

  /**
   * Send raw audio bytes over WebSocket
   */
  private sendAudioData(buffer: ArrayBuffer) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(buffer);
  }

  /**
   * Play audio immediately - simple and direct
   */
  private playAudioImmediately(audioData: Float32Array): void {
    if (!this.audioContext) return;

    try {
      const audioBuffer = this.audioContext.createBuffer(
        DEFAULT_OPUS_PACKAGE_CONFIG.channels,
        audioData.length,
        DEFAULT_OPUS_PACKAGE_CONFIG.sampleRate,
      );
      const channelData = audioBuffer.getChannelData(0);
      channelData.set(audioData);

      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;

      // connect through gain node
      if (this.audioGainNode) {
        source.connect(this.audioGainNode);
      } else {
        source.connect(this.audioContext.destination);
      }

      source.start();
    } catch (error) {
      console.warn('Simple audio playback failed:', error);
    }
  }

  /**
   * Ensure audio context is in proper state for playback
   */
  private async ensureAudioContextReady(): Promise<void> {
    if (!this.audioContext) return;

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  /**
   * Decode Opus frame with the same lib the server uses and play it
   */
  private async decodeAndPlayFromServer(opusFrame: ArrayBuffer) {
    if (!this.opusDecoder) return;

    await this.ensureAudioContextReady();
    const u8 = new Uint8Array(opusFrame);

    const raw = this.decodeWithopusDecoder(u8);
    if (!raw) {
      console.warn('Opus decode returned null/empty');
      return;
    }

    const f32 = normalizeToF32(raw);
    if (f32) {
      if (this.playbackNode) {
        this.playbackNode.port.postMessage({ type: 'pcmF32', buffer: f32 }, [f32.buffer]);
      } else {
        // fallback to immediate playback if worklet not available
        this.playAudioImmediately(f32);
      }
    } else {
      console.warn('Failed to normalize audio frame');
    }
  }

  /**
   * Helper wrapper around 'opus-decoder' variants
   */
  private decodeWithopusDecoder(packet: Uint8Array) {
    const dec: any = this.opusDecoder;
    try {
      let result;
      result = dec.decodeFrame(packet);

      return result;
    } catch (e) {
      console.warn('opus-decoder decode failed', e);
      return null;
    }
  }

  /**
   * Start a call (connect WS + init audio path)
   */
  async startCall(): Promise<void> {
    const epoch = this.lifecycle;
    await this.connect();

    if (!this.ws || epoch !== this.lifecycle) return;
    if (this.config.visitor?.mode === 'chat') return;
    await this.initializeAudioCapture(epoch);
  }

  sendText(id: string, text: string): boolean {
    if (
      !this.config.visitor ||
      this.config.visitor.mode !== 'chat' ||
      !this.isSessionReady ||
      !text.trim() ||
      text.length > 4000 ||
      !this.ws ||
      this.ws.readyState !== WebSocket.OPEN
    )
      return false;
    this.send('customer.text', { id, text });
    return true;
  }

  /**
   * Stop audio capture and close WS
   */
  async endCall(): Promise<void> {
    this.lifecycle++;
    this.isRecording = false;
    // Revoke transport and capture ownership before the first asynchronous cleanup.
    const socket = this.ws;
    this.ws = null;
    const stream = this.mediaStream;
    this.mediaStream = null;
    const context = this.audioContext;
    this.audioContext = null;
    const reader = this.micReader;
    this.micReader = null;
    const cleanup = this.captureCleanup;
    this.captureCleanup = undefined;
    cleanup?.();
    const decoder = this.opusDecoder;
    this.opusDecoder = undefined;
    this.isSessionReady = false;
    this.callId = null;
    this.micFrameLength = 0;
    this.hasRetriedAuth = false;
    this.lastExceptionType = null;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      try {
        socket.close();
      } catch {}
    }
    try {
      stream?.getTracks().forEach((track) => track.stop());
    } catch {}
    if (reader) void reader.cancel().catch(() => {});
    if (this.captureNode) {
      this.captureNode.port.onmessage = null;
      try {
        this.captureNode.disconnect();
      } catch {}
      this.captureNode = null;
    }
    if (this.playbackNode) {
      try {
        this.playbackNode.disconnect();
      } catch {}
      this.playbackNode = null;
    }
    if (this.audioGainNode) {
      try {
        this.audioGainNode.disconnect();
      } catch {}
      this.audioGainNode = null;
    }
    if (this.opusEncoder) {
      try {
        this.opusEncoder.destroy();
      } catch {}
      this.opusEncoder = undefined;
    }
    try {
      decoder?.free();
    } catch {}
    this.mediaOutDest = null;
    if (this.remoteAudioEl) {
      this.remoteAudioEl.srcObject = null;
    }
    if (socket) this.emit('disconnect');
    if (context) {
      try {
        await context.close();
      } catch {}
    }
  }

  /**
   * Set audio gain for smoother output
   */
  setAudioGain(gain: number): void {
    if (this.audioGainNode) {
      this.audioGainNode.gain.value = Math.max(0, Math.min(1, gain));
    }
  }

  /**
   * Destroy the service and remove listeners
   */
  destroy(): void {
    this.endCall();
    this.removeAllListeners();
  }
}
